import { MeetingTemplateList } from '@wundo/ui';
import type {
  MeetingTemplateItem,
  MeetingTemplateKind,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { reorderWithinKind } from '@wundo/shared/reorderWithinKind.ts';
import { meetingTemplateLabels } from '@/lib/meetingTemplateLabels.ts';
import { meetingTemplateRow } from '@/lib/meetingTemplateRow.ts';
import { m } from '@/paraglide/messages.js';
import type { MeetingTemplateReorder } from './useMeetingTemplateReorder.ts';

const MEETING_TEMPLATE_KINDS: MeetingTemplateKind[] = ['one_on_one', 'team', 'other'];

interface MeetingTemplateBlocksProps {
  templates: MeetingTemplateItem[];
  /** Whoever keeps the organisation's types may arrange them too. */
  keepsOrganisation: boolean;
  /** The last move was refused, so the list went back to what the server holds. */
  moveFailed: boolean;
  onReorder: (change: MeetingTemplateReorder) => void;
  onOpen: (templateId: string) => void;
  onCreate: (kind: MeetingTemplateKind) => void;
}

/** A block per kind of meeting, the organisation's types first, then your own. */
export function MeetingTemplateBlocks({
  templates,
  keepsOrganisation,
  moveFailed,
  onReorder,
  onOpen,
  onCreate,
}: MeetingTemplateBlocksProps) {
  const titles: Record<MeetingTemplateKind, string> = {
    one_on_one: m.meeting_kind_one_on_one(),
    team: m.meetings_kind_team(),
    other: m.settings_templates_other(),
  };

  /*
   * One run of a block: the rows of one scope and one kind. The server keeps
   * one order per scope, so a move inside a kind is sent as that whole scope.
   */
  const run = (meetingKind: MeetingTemplateKind, scope: MeetingTemplateScope) => {
    const scoped = templates.filter((template) => template.scope === scope);
    return {
      key: scope,
      templates: scoped
        .filter((template) => template.kind === meetingKind)
        .map(meetingTemplateRow),
      /* Your own types are yours to arrange, always. */
      canReorder: scope === 'personal' || keepsOrganisation,
      onReorder: (ids: string[]) =>
        onReorder({ scope, ids: reorderWithinKind(scoped, meetingKind, ids) }),
    };
  };

  return (
    <MeetingTemplateList
      layout='blocks'
      /* The list went back to what the server holds; say why. */
      error={moveFailed ? m.settings_templates_move_failed() : undefined}
      onOpen={onOpen}
      labels={meetingTemplateLabels()}
      sections={MEETING_TEMPLATE_KINDS.map((meetingKind) => ({
        key: meetingKind,
        title: titles[meetingKind],
        emptyMessage: m.settings_templates_empty(),
        runs: [run(meetingKind, 'organisation'), run(meetingKind, 'personal')],
        canCreate: true,
        createLabel: m.settings_templates_new_label({ kind: titles[meetingKind] }),
        onCreate: () => onCreate(meetingKind),
      }))}
    />
  );
}
